import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import ListingDetails from './ListingDetails';
import ListingCard from './components/ListingCard';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import HomePage from './components/HomePage';

/**
 * Main application component that serves as the entry point for the React application.
 * 
 * Features:
 * - Manages application state and data fetching
 * - Implements routing using React Router
 * - Handles loading and error states
 * - Provides search functionality with debouncing
 * - Displays featured, recent, and top-rated listings
 * 
 * @component
 * @example
 * return (
 *   <App />
 * )
 */
function App() {
  const cancelRequest = React.useRef(null);
  
  // Application state management
  // Removed unused listings state
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [searchQuery, setSearchQuery] = useState(''); // Raw search query
  const [debouncedQuery, setDebouncedQuery] = useState(''); // Debounced search query

  /**
   * Debounces the search input to prevent excessive API calls
   * Updates debouncedQuery 300ms after the user stops typing
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Listing categories
  const [featured, setFeatured] = useState([]); // Featured listings
  const [recent, setRecent] = useState([]); // Recently added listings
  const [topRated, setTopRated] = useState([]); // Top rated listings

  /**
   * Fetches listings from the backend API with optional sorting and filtering
   * 
   * @param {string|null} sortBy - Sorting criteria ('recent' or 'top')
   * @param {boolean|null} filterFeatured - Whether to filter featured listings
   * @returns {Promise<Array>} Array of listing objects
   * @throws {Error} If the API request fails
   */
  const fetchListings = React.useCallback(async (sortBy = null, filterFeatured = null, searchQuery = '') => {
    try {
      const params = {
        ...(sortBy && { sort_by: sortBy }),
        ...(filterFeatured && { filter_featured: filterFeatured }),
        ...(searchQuery && { search: searchQuery })
      };
      
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/listings`, { 
        params,
        cancelToken: new axios.CancelToken(c => cancelRequest.current = c)
      });
      return response.data;
    } catch (err) {
      if (!axios.isCancel(err)) {
        throw err;
      }
    }
  }, []);

  const filterListings = React.useCallback((listings, query) => {
    if (!query) return listings;
    
    const lowerQuery = query.toLowerCase();
    return listings.filter(listing => {
      const fieldsToSearch = [
        listing.properties['AI Product Name']?.title?.[0]?.plain_text,
        listing.properties.Summary?.rich_text?.[0]?.plain_text,
        listing.properties['Key Features']?.rich_text?.[0]?.plain_text,
        listing.properties.Category?.rich_text?.[0]?.plain_text,
        listing.properties.Tags?.rich_text?.[0]?.plain_text
      ];
      
      return fieldsToSearch.some(field => 
        field?.toLowerCase().includes(lowerQuery)
      );
    });
  }, []);

  // Handle search functionality
  useEffect(() => {

    if (debouncedQuery) {
      setLoading(true);
      try {
        const filteredFeatured = filterListings(featured, debouncedQuery);
        const filteredRecent = filterListings(recent, debouncedQuery);
        const filteredTopRated = filterListings(topRated, debouncedQuery);
        
        setFeatured(filteredFeatured);
        setRecent(filteredRecent);
        setTopRated(filteredTopRated);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      // Reset to original listings when search is cleared
      const loadOriginalListings = async () => {
        try {
          setLoading(true);
          const featured = await fetchListings(null, true);
          const recent = await fetchListings('recent');
          const topRated = await fetchListings('top');
          
          setFeatured(featured.slice(0, 5));
          setRecent(recent.slice(0, 5));
          setTopRated(topRated.slice(0, 5));
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      loadOriginalListings();
    }
  }, [debouncedQuery]);

  // Client-side cache
  const [cache, setCache] = useState({
    featured: null,
    recent: null,
    topRated: null,
    lastUpdated: null
  });

  /**
   * Initial data loading effect
   * Shows cached data immediately while fetching fresh data
   */
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Show cached data if available
        if (cache.featured) setFeatured(cache.featured);
        if (cache.recent) setRecent(cache.recent);
        if (cache.topRated) setTopRated(cache.topRated);

        // Fetch fresh data in background
        const [featured, recent, topRated] = await Promise.all([
          fetchListings(null, true),
          fetchListings('recent'),
          fetchListings('top')
        ]);

        // Update state and cache
        const newFeatured = featured.slice(0, 5);
        const newRecent = recent.slice(0, 5);
        const newTopRated = topRated.slice(0, 5);
        
        setFeatured(newFeatured);
        setRecent(newRecent);
        setTopRated(newTopRated);
        setCache({
          featured: newFeatured,
          recent: newRecent,
          topRated: newTopRated,
          lastUpdated: Date.now()
        });
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Loading and error states are now handled within individual route components

  // Main application UI
  return (
    <ErrorBoundary>
      <Router>
        <div className="min-h-screen flex flex-col bg-background">
          <Header onSearchChange={(e) => setSearchQuery(e.target.value)} />
          <Routes>
            {/* Home route with search and listing sections */}
            <Route path="/" element={
              <HomePage 
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                featured={featured}
                recent={recent}
                topRated={topRated}
                loading={loading}
                error={error}
              />
            } />
          {/* Listing details route */}
          <Route 
            path="/listings/:idOrSlug" 
            element={<ListingDetails />} 
          />
          {/* Featured listings route */}
          <Route
            path="/featured"
            element={
              <div className="container mx-auto px-4 py-8">
                <div className="flex items-center gap-2 mb-8">
                  <div className="icon-container featured">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                  </div>
                  <h1 className="text-2xl font-bold">Featured Listings</h1>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {featured.map(listing => (
                    <ListingCard key={listing.id} listing={listing} />
                  ))}
                </div>
              </div>
            }
          />
          {/* Top listings route */}
          <Route
            path="/top"
            element={
              <div className="container mx-auto px-4 py-8">
                <div className="flex items-center gap-2 mb-8">
                  <div className="icon-container top-rated">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trending-up">
                      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                      <polyline points="16 7 22 7 22 13"></polyline>
                    </svg>
                  </div>
                  <h1 className="text-2xl font-bold">Top Listings</h1>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {topRated.map(listing => (
                    <ListingCard key={listing.id} listing={listing} />
                  ))}
                </div>
              </div>
            }
          />
          {/* About page route */}
          <Route path="/about" element={<AboutPage />} />
          {/* Privacy page route */}
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Terms page route */}
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
